import React, { forwardRef, useEffect, useRef } from 'react';
import ChatHistoryList from './ChatHistoryList';
import InputChat from './InputChat';
import { useSelector } from 'react-redux';
import { showSuccessMessage, showWarningMessage } from '../../../../utils/showMessage';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd'
/**
 * ChatContainer
 * - Mostra il banner di lock solo quando isWritable === false
 * - Mostra i toast solo dal secondo aggiornamento in poi
 */
const ChatContainer = (
  {
    isLoading,
    isHistoryLoading,
    onRegenerateMessage,
    chatHistory,
    onSubmit,
    onAddNote = () => {},
    chatID,
    openReferenceRegulationModal = () => {},
    refetch = () => {},
  },
  ref,
) => {
  const token = useSelector((state) => state.user.access_token);
  const [isWritable, setIsWritable] = React.useState(null); // null => stato non ancora noto
  const { t } = useTranslation();

  // tiene l'ultimo valore noto di isWritable
  const lastWritableRef = useRef(null);

  useEffect(() => {
    setIsWritable(null);
    console.log('Connessione WebSocket in corso...');
    const socket = new WebSocket(`${process.env.REACT_APP_WS_URL}/chats/${chatID}/ws`);

    socket.onopen = () => {
      console.log('Connessione WebSocket aperta');
      socket.send(
        JSON.stringify({
          type: 'auth',
          token,
        }),
      );
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type !== 'msg') return;

      const nextWritable = data.data.isWritable; // boolean
      const prevWritable = lastWritableRef.current; // null | boolean

      // toast solo dopo la prima notifica
      if (prevWritable !== null && nextWritable !== prevWritable) {
        nextWritable
          ? showSuccessMessage(t('Ora puoi interagire con la chat'))
          : showWarningMessage(t('Un altro utente sta interagendo con la chat'));
      }

      setIsWritable(nextWritable);
      lastWritableRef.current = nextWritable;
    };

    return () => {
      console.log('Connessione WebSocket chiusa');
      socket.close();
    };
  }, []); // una sola volta al mount

  // Refetch della chat se non è scrivibile
  useEffect(() => {
    let interval = null;
    if (isWritable !== null) {
      if (isWritable) {
        clearInterval(interval);
      } else {
        interval = setInterval(() => {
          refetch();
        }, 5000);
      }
    }

    return () => clearInterval(interval);
  }, [isWritable, refetch]);

  return (
    <>
      {/* Banner di lock: visibile solo se sappiamo che la chat è bloccata */}
      {isWritable === false && (
        <div>
          <div
            className="flex items-center p-4 mt-4 text-sm text-black rounded-lg bg-yellow-50 border-[1px] border-solid border-yellow-300"
            role="alert"
          >
            <svg
              className="shrink-0 inline w-4 h-4 me-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
            </svg>
            <span className="sr-only">Info</span>
            <div className="text-[0.8rem]">
              <span className="font-medium">Un altro utente sta interagendo con la chat.</span>{' '}
              Sei in coda come spettatore. Riceverai una notifica quando sarà il tuo turno.
            </div>
          </div>
        </div>
      )}

      {/* Chat Wrapper */}

       {isHistoryLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spin />
        </div>
      )}

      <div
        ref={ref}
        className="pt-5 flex flex-col flex-auto w-full h-full max-h-[calc(100vh-240px)] gap-6 overflow-y-auto"
      >
        <ChatHistoryList
          onAddNote={onAddNote}
          isLoading={isLoading}
          onRegenerateMessage={onRegenerateMessage}
          chatHistory={chatHistory}
          isWritable={isWritable}
          openReferenceRegulationModal={openReferenceRegulationModal}
        />
      </div>

      {/* Input Chat */}
      <div className="w-full">
        <InputChat onSubmit={onSubmit} isLoading={isLoading} isWritable={isWritable} />
      </div>
    </>
  );
};

export default forwardRef(ChatContainer);
